import * as React from 'react';
import { Button, Col, Row, FormControl, InputGroup } from 'react-bootstrap';

interface NumberInputWithKeypadProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  inputMode?: 'numeric' | 'tel';
  autoComplete?: string;
  pattern?: string;
  /**
   * Optional callback fired when the user presses the Enter/Return key on the
   * keyboard (or the "Go" action on mobile keyboards).
   */
  onEnter?: () => void;
}

const NumberInputWithKeypad: React.FC<NumberInputWithKeypadProps> = ({
  value,
  onChange,
  placeholder = "Enter number",
  maxLength,
  disabled = false,
  inputMode = 'numeric',
  autoComplete,
  pattern,
  onEnter,
}) => {
  const handleNumberClick = (num: number | string) => {
    if (!maxLength || value.length < maxLength) {
      onChange(value + num);
    }
  };

  const handleBackspace = () => {
    onChange(value.slice(0, -1));
  };

  const handleClear = () => {
    onChange('');
  };

  const renderNumRow = (numbers: number[]) => {
    return (
      <Row>
        {numbers.map(num => (
          <Col xs={4} key={num} className="text-center">
            <Button 
              className="num" 
              variant="outline-info" 
              onClick={() => handleNumberClick(num)}
              disabled={disabled || (maxLength ? value.length >= maxLength : false)}
            >
              {num}
            </Button>
          </Col>
        ))}
      </Row>
    );
  };

  const renderLastRow = () => {
    return (
      <Row>
        <Col xs={4} className="text-center">
          <Button 
            className="num" 
            variant="outline-danger" 
            onClick={handleClear}
            disabled={disabled}
          >
            X
          </Button>
        </Col>
        <Col xs={4} className="text-center">
          <Button 
            className="num" 
            variant="outline-info" 
            onClick={() => handleNumberClick(0)}
            disabled={disabled || (maxLength ? value.length >= maxLength : false)}
          >
            0
          </Button>
        </Col>
        <Col xs={4} className="text-center">
          <Button 
            className="num" 
            variant="outline-warning" 
            onClick={handleBackspace}
            disabled={disabled}
          >
            ⌫
          </Button>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <Row className="mb-4">
        <Col>
          <InputGroup size="lg">
            <FormControl 
              aria-label="Number Input" 
              aria-describedby="inputGroup-sizing-sm"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                  // Prevent form submission if inside a form
                  e.preventDefault();
                  onEnter?.();
                }
              }}
              placeholder={placeholder}
              maxLength={maxLength}
              inputMode={inputMode}
              autoComplete={autoComplete}
              pattern={pattern}
              disabled={disabled}
            />
          </InputGroup>
        </Col>
      </Row>
      {renderNumRow([1, 2, 3])}
      {renderNumRow([4, 5, 6])}
      {renderNumRow([7, 8, 9])}
      {renderLastRow()}
    </>
  );
};

export default NumberInputWithKeypad; 